import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  ACTIVE_OPTIONS_SOURCES,
  TActiveOptionsSource,
  TBroadcastActiveOptions,
  TMentionFacets,
} from './active-options';

type UseMentionFacetsRange = {
  source: TActiveOptionsSource;
  gte?: string;
  lte?: string;
};

/** What a dropdown reads from the workspace and tag ids of its source, however they come. */
export type TFacetsResult = { data?: TMentionFacets; isFetching: boolean; isError: boolean };

/** The workspace and tag ids the rows of a source's range mention. */
export function useMentionFacets(
  { source, gte, lte }: UseMentionFacetsRange,
  config: { enabled?: boolean; staleTime?: number } = {}
) {
  const { facets } = ACTIVE_OPTIONS_SOURCES[source];
  const range = { gte, lte };

  return useQuery({
    // Both facets endpoints refuse a request without any bound.
    enabled: Boolean(gte || lte),
    staleTime: facets.staleTime(range),
    ...config,
    queryKey: facets.queryKey(range),
    queryFn: () => facets.fetch(range),
  });
}

/** The workspace and tag ids a broadcast source already carries, with no request. */
export function useBroadcastFacets({
  ids,
  isFetching,
  isError,
}: TBroadcastActiveOptions): TFacetsResult {
  const data = useMemo(() => ids && { workspaces: ids.workspaces, tags: ids.tags }, [ids]);
  return { data, isFetching, isError };
}
